import React from 'react';
import Modal from 'react-modal';
import CategoryForm from './CategoryForm';
import AdminButton from '../../../components/admin/AdminButton';
import { getSupabase } from '../../../supabaseClient';
import toast from 'react-hot-toast';
import { useEffectOnce } from '../../../hooks/useEffectOnce';
import { useAuth } from '../../../contexts/AuthContext';
//import DOMPurify from 'dompurify';

// Bind modal to your appElement for accessibility
Modal.setAppElement('#root');

const CategoriesList = () => {
  // Fonction pour tronquer le texte HTML
/*const truncateHtml = (html, maxLength = 200) => {
  if (!html) return '';
  // Créer un élément temporaire pour extraire le texte
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = DOMPurify.sanitize(html);
  let text = tempDiv.textContent || tempDiv.innerText || '';
  // Tronquer le texte
  if (text.length > maxLength) {
    text = text.substr(0, maxLength) + '...';
  }
  return text;
};*/

// États du composant
  const [categories, setCategories] = React.useState([]);
  const [services, setServices] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedService, setSelectedService] = React.useState('');
  const [selectedParentCategory, setSelectedParentCategory] = React.useState('');
  const [modalIsOpen, setModalIsOpen] = React.useState(false);
  const [selectedCategory, setSelectedCategory] = React.useState(null);
  const [confirmModalIsOpen, setConfirmModalIsOpen] = React.useState(false);
  const [categoryToToggle, setCategoryToToggle] = React.useState(null);
  
  // Récupérer l'utilisateur connecté
  const { user } = useAuth();
  
  // Vérifier si l'utilisateur est restreint
  const isRestrictedUser = user?.email === 'contact@cgci-paris.com';

  // Récupérer les catégories avec leurs détails
  const fetchCategoriesWithDetails = React.useCallback(async () => {
    try {
      const supabase = getSupabase();
      
      // Récupérer les catégories avec leurs services et actes consulaires
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('categories')
        .select(`
          *,
          services (
            id,
            name
          ),
          consular_acts (
            id,
            title
          )
        `);
      
      // Récupérer les services
      const { data: servicesData, error: servicesError } = await supabase
        .from('services')
        .select('id, name');
      
      if (categoriesError || servicesError) {
        throw categoriesError || servicesError;
      }
      
      // Transformer les données pour avoir le nombre de services et d'actes
      const enrichedCategories = categoriesData.map(category => ({
        ...category,
        servicesCount: category.services ? category.services.length : 0,
        actsCount: category.consular_acts ? category.consular_acts.length : 0,
        serviceName: category.services?.length > 0 ? category.services[0].name : 'Non assigné'
      }));
      
      setCategories(enrichedCategories);
      setServices(servicesData || []);
      
      // Afficher un toast si aucune catégorie n'est trouvée
      if (!enrichedCategories || enrichedCategories.length === 0) {
        toast.info('Aucune catégorie trouvée');
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des catégories:', err);
      toast.error(`Erreur : ${err.message}`);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger les catégories une seule fois au montage
  useEffectOnce(() => {
    fetchCategoriesWithDetails();
  });

  // Gérer la recherche de catégories
  const handleSearch = React.useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  // Filtrer les catégories
  const filteredCategories = React.useMemo(() => {
    return categories
      .filter(category => 
        // Filtrer par nom
        category.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        // Filtrer par service si sélectionné
        (selectedService === '' || category.service_id === selectedService) &&
        // Filtrer par catégorie parente si sélectionnée
        (selectedParentCategory === '' || category.parent_id === selectedParentCategory)
      )
      .reverse(); // Inverser l'ordre des catégories
  }, [categories, searchTerm, selectedService, selectedParentCategory]);

  // Gérer l'édition d'une catégorie
  const handleEdit = React.useCallback((category) => {
    // S'assurer que toutes les propriétés nécessaires sont présentes
    setSelectedCategory({
      id: category.id,
      name: category.name,
      description: category.description,
      service_id: category.service_id,
      parent_id: category.parent_id,
      active: category.active
    });
    setModalIsOpen(true);
  }, []);

  // Gérer le changement d'état (actif/inactif) d'une catégorie
  const handleToggleActive = React.useCallback(async () => {
    if (!categoryToToggle) return;
    
    const toastId = toast.loading(`${categoryToToggle.active ? 'Désactivation' : 'Activation'} de la catégorie en cours...`);
    try {
      const { error } = await getSupabase()
        .from('categories')
        .update({ active: !categoryToToggle.active })
        .eq('id', categoryToToggle.id);

      if (error) throw error;
      
      // Mettre à jour la liste des catégories après modification
      await fetchCategoriesWithDetails();
      toast.success(`La catégorie a été ${categoryToToggle.active ? 'désactivée' : 'activée'} avec succès`, { id: toastId });
    } catch (err) {
      console.error(`Erreur lors du changement d'état de la catégorie:`, err);
      toast.error(`Erreur : ${err.message}`, { id: toastId });
    } finally {
      setConfirmModalIsOpen(false);
      setCategoryToToggle(null);
    }
  }, [categoryToToggle, fetchCategoriesWithDetails]);

  // Ouvrir la modale de confirmation pour le toggle
  const openToggleConfirmModal = React.useCallback((category) => {
    setCategoryToToggle(category);
    setConfirmModalIsOpen(true);
  }, []);

  // Fermer la modale de confirmation pour le toggle
  const closeToggleConfirmModal = React.useCallback(() => {
    setConfirmModalIsOpen(false);
    setCategoryToToggle(null);
  }, []);

  // Gérer la suppression d'une catégorie
  const handleDelete = React.useCallback(async (categoryId) => {
    const toastId = toast.loading('Suppression de la catégorie...');
    try {
      const { error } = await getSupabase()
        .from('categories')
        .delete()
        .eq('id', categoryId);

      if (error) throw error;
      
      // Mettre à jour la liste des catégories après suppression
      setCategories(categories.filter(category => category.id !== categoryId));
      toast.success('Catégorie supprimée avec succès', { id: toastId });
    } catch (error) {
      console.error('Erreur lors de la suppression de la catégorie :', error);
      toast.error(`Erreur lors de la suppression : ${error.message}`, { id: toastId });
    }
  }, [categories]);

  // Rendu du contenu
  const renderContent = React.useCallback(() => {
    // État de chargement
    if (loading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-secondary"></div>
        </div>
      );
    }

    // Aucune catégorie trouvée
    if (filteredCategories.length === 0) {
      return (
        <div className="text-center py-10 text-gray-500">
          <p className="text-lg">
            {searchTerm || selectedService || selectedParentCategory
              ? 'Aucune catégorie ne correspond aux filtres sélectionnés'
              : 'Aucune catégorie n\'a été trouvée'}
          </p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-0">
        {filteredCategories.map((category) => (
          <div
            key={category.id}
            className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 flex flex-col h-full"
          >
            {/* Icône et titre */}
            <div className="p-6 bg-[#e88c00]/10 border-b border-gray-200">
              <div className="flex items-center space-x-4">
                <div className="bg-primary p-3 rounded-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{category.name}</h3>
                </div>
              </div>
            </div>
            
            {/* Description */}
            <div className="p-6 flex-grow">
              <h3 className="text-sm font-bold mb-2">{category.services ? category.services.name : 'Non assigné'}</h3>
              <p className="text-gray-600 mb-6">
                {category.description || 'Aucune description disponible'}
              </p>
              
              {/* Compteurs */}
              <div className="flex justify-between mb-2 text-sm">
                <div className="flex items-center bg-gray-100 rounded-full p-2 space-x-2">
                  <span className="font-medium text-gray-700">acte(s)</span>
                  <span className="ml-2 bg-secondary text-white font-bold px-2 py-1 rounded-full">
                    {category.actsCount}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Boutons d'action */}
            <div className="bg-gray-100 px-6 py-3 flex justify-between items-center border-t border-gray-200 mt-auto">
              {!isRestrictedUser ? (
                <>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => openToggleConfirmModal(category)}
                      className={`flex items-center px-4 py-2 ${category.active ? 'bg-green-600' : 'bg-gray-500'} text-white rounded-md hover:opacity-90 transition-colors`}
                      title={category.active ? 'Désactiver la catégorie' : 'Activer la catégorie'}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={category.active ? "M5 13l4 4L19 7" : "M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"} />
                      </svg>
                      {category.active ? 'Actif' : 'Inactif'}
                    </button>
                    <button 
                      onClick={() => handleEdit(category)}
                      className="flex items-center px-4 py-2 bg-secondary text-white rounded-md hover:bg-opacity-90 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Modifier
                    </button>
                  </div>
                  <button 
                    onClick={() => handleDelete(category.id)}
                    className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </>
              ) : (
                <div className="w-full text-center text-gray-500 text-sm">
                  {category.active ? 'Catégorie active' : 'Catégorie inactive'}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredCategories, loading, handleEdit, handleDelete]);

  // Fonction pour ouvrir la modale
  const openModal = React.useCallback(() => {
    setModalIsOpen(true);
  }, []);

  // Fonction pour fermer la modale
  const closeModal = React.useCallback(() => {
    setModalIsOpen(false);
    setSelectedCategory(null);
  }, []);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Gestion des catégories</h2>
        
        {!isRestrictedUser && (
          <AdminButton 
            type="primary" 
            onClick={() => {
              setSelectedCategory(null);
              openModal();
            }}
            icon="add"
          >
            Nouvelle catégorie
          </AdminButton>
        )}
      </div>
      
      {/* Filtres et barre de recherche */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Barre de recherche */}
        <div className="relative">
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Rechercher une catégorie..."
            className="tracking-input pl-10 w-full form-input"
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>

        {/* Filtre par service */}
        <select
          className="tracking-input w-full form-input"
          value={selectedService}
          onChange={(e) => setSelectedService(e.target.value)}
        >
          <option value="">Tous les services</option>
          {services.map(service => (
            <option key={service.id} value={service.id}>
              {service.name}
            </option>
          ))}
        </select>

        {/* Filtre par catégorie parente */}
        <select
          className="tracking-input w-full form-input"
          value={selectedParentCategory}
          onChange={(e) => setSelectedParentCategory(e.target.value)}
        >
          <option value="">Toutes les catégories</option>
          {categories.map(category => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
      </div>
      
      {renderContent()}
      
      {/* Modal pour le formulaire de catégorie avec react-modal */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Formulaire de catégorie"
        overlayClassName="ReactModal__Overlay"
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        ariaHideApp={false}
      >
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-xl font-bold text-gray-900">
            {selectedCategory ? 'Modifier une catégorie' : 'Nouvelle catégorie'}
          </h3>
          <button 
            type="button"
            onClick={closeModal}
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div className="p-6">
          <CategoryForm 
            isModal={true}
            onSuccess={() => {
              closeModal();
              fetchCategoriesWithDetails();
            }}
            initialData={selectedCategory}
          />
        </div>
      </Modal>

      {/* Modale de confirmation pour activer/désactiver une catégorie */}
      <Modal
        isOpen={confirmModalIsOpen}
        onRequestClose={closeToggleConfirmModal}
        contentLabel="Confirmation de changement d'état"
        overlayClassName="ReactModal__Overlay"
        className="max-w-md w-full bg-white rounded-xl shadow-lg overflow-hidden flex flex-col"
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        ariaHideApp={false}
      >
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-xl font-bold text-gray-900">
            Confirmation
          </h3>
          <button 
            type="button"
            onClick={closeToggleConfirmModal}
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div className="p-6">
          <p className="text-gray-700 mb-6">
            {categoryToToggle?.active 
              ? `Êtes-vous sûr de vouloir désactiver la catégorie "${categoryToToggle?.name}" ?` 
              : `Êtes-vous sûr de vouloir activer la catégorie "${categoryToToggle?.name}" ?`}
          </p>
          <div className="flex justify-end space-x-4">
            <button
              onClick={closeToggleConfirmModal}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleToggleActive}
              className={`px-4 py-2 ${categoryToToggle?.active ? 'bg-orange-500' : 'bg-green-600'} text-white rounded-md hover:opacity-90 transition-colors`}
            >
              {categoryToToggle?.active ? 'Désactiver' : 'Activer'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CategoriesList;
